import {
  Building2,
  ChevronRight,
  Folder,
  FolderOpen,
  House,
  LayoutDashboard,
  LayoutGrid,
  Sparkles,
  Undo2,
  Users,
} from 'lucide-react';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, Skeleton } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { useDismissModalOnRouteChange } from '../hooks';
import { useDashboardData } from '../context';
import DashboardButton from './DashboardButton';

const buildPath = (stack, selectedItem) => [...stack.map((entry) => entry.name), selectedItem.name].join(' / ');

/** Stable ids from defaultDataAdapters + common API slugs; falls back to name (lowercase). */
const SECTION_ICON_BY_ID = {
  home: House,
  contacts: Users,
  workspace: LayoutGrid,
  'contacts-ucp': Building2,
  'home-overview': LayoutDashboard,
  'workspace-overview': LayoutDashboard,
  'ucp-overview': LayoutDashboard,
  'ucp-intelligence': Sparkles,
};

const SECTION_ICON_BY_NAME = {
  home: House,
  contacts: Users,
  workspace: LayoutGrid,
  ucp: Building2,
  overview: LayoutDashboard,
  intelligence: Sparkles,
};

function resolveLocationItemIcon(item) {
  const id = item?.id != null ? String(item.id) : '';
  if (id && SECTION_ICON_BY_ID[id]) return SECTION_ICON_BY_ID[id];
  const name = String(item?.name ?? '')
    .trim()
    .toLowerCase();
  if (name && SECTION_ICON_BY_NAME[name]) return SECTION_ICON_BY_NAME[name];
  return null;
}

const SelectLocationModal = ({ open = false, onClose, onSelect }) => {
  const { t } = useTranslation('dashboards');
  const { sections, loadingSections, fetchSections } = useDashboardData();
  const [navigationStack, setNavigationStack] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);

  const resetAndClose = useCallback(() => {
    setNavigationStack([]);
    setSelectedItem(null);
    onClose?.();
  }, [onClose]);

  useEffect(() => {
    if (open) fetchSections();
  }, [fetchSections, open]);

  useDismissModalOnRouteChange(open, resetAndClose);

  const currentItems = useMemo(() => {
    if (!navigationStack.length) return sections;
    return navigationStack[navigationStack.length - 1]?.children || [];
  }, [navigationStack, sections]);

  const breadcrumb = useMemo(() => navigationStack.map((entry) => entry.name).join(' / '), [navigationStack]);
  const isRootLevel = navigationStack.length === 0;

  return (
    <Modal
      isOpen={open}
      onClose={resetAndClose}
      title={t('locationPicker.title')}
      size="md"
      portalRootId="shell-main-content"
      className="!max-w-[min(692px,calc(100%-2rem))]"
    >
      <div className="flex min-h-[340px] flex-col gap-6">
        <p className="text-sm font-medium text-content-muted">{t('locationPicker.description')}</p>

        {!isRootLevel && (
          <div className="flex items-center justify-between gap-3">
            <DashboardButton
              variant="ghost"
              size="sm"
              leftIcon={Undo2}
              className="font-medium text-content-secondary"
              onClick={() => {
                setNavigationStack((prev) => prev.slice(0, -1));
                setSelectedItem(null);
              }}
            >
              {t('locationPicker.backButton')}
            </DashboardButton>
            <p className="truncate text-right text-sm text-content-muted">{breadcrumb}</p>
          </div>
        )}

        <div className="scrollbar-aimsos-auto-sm flex max-h-56 min-h-56 flex-1 flex-col overflow-y-auto">
          {loadingSections && sections.length === 0 ? (
            Array.from({ length: 7 }).map((_, index) => (
              <div key={`section-skeleton-${index}`} className="py-0.5">
                <Skeleton height="32px" width="100%" borderRadius="0" />
              </div>
            ))
          ) : currentItems.length === 0 ? (
            /* A section catalog that came back empty used to render a blank
               box with a disabled Select underneath, which reads as a broken
               modal rather than as "there is nothing here". */
            <p className="m-0 flex flex-1 items-center justify-center px-6 text-center text-xs text-content-muted">
              {t('locationPicker.empty')}
            </p>
          ) : (
            currentItems.map((item) => {
              const hasChildren = Array.isArray(item.children) && item.children.length > 0;
              const isSelected = selectedItem?.id === item.id;
              const SemanticIcon = resolveLocationItemIcon(item);
              const FallbackIcon = isRootLevel ? Folder : FolderOpen;
              const Icon = SemanticIcon ?? FallbackIcon;
              const iconClass = SemanticIcon
                ? isRootLevel
                  ? 'shrink-0 text-brand-600 dark:text-brand-400'
                  : 'shrink-0 text-content-muted'
                : isRootLevel
                  ? 'shrink-0 text-brand-600'
                  : 'shrink-0 text-content-muted';
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => {
                    if (hasChildren) {
                      setNavigationStack((prev) => [...prev, item]);
                      setSelectedItem(null);
                      return;
                    }
                    if (!item.supportsDashboard) return;
                    setSelectedItem((prev) => (prev?.id === item.id ? null : item));
                  }}
                  className={`flex h-8 items-center justify-between px-3 text-left transition-colors hover:bg-surface-hover-subtle ${
                    isSelected ? 'bg-brand-50 dark:bg-brand-950/40' : ''
                  }`}
                >
                  <span className="flex min-w-0 items-center gap-2">
                    <Icon size={14} className={iconClass} strokeWidth={2} />
                    <span className="truncate text-xs font-semibold text-content-secondary">{item.name}</span>
                  </span>
                  {hasChildren && <ChevronRight size={14} className="text-content-muted" />}
                </button>
              );
            })
          )}
        </div>
      </div>

      <Modal.Footer>
        <DashboardButton variant="secondary" onClick={resetAndClose}>
          {t('locationPicker.cancelButton')}
        </DashboardButton>
        <DashboardButton
          onClick={() => {
            if (!selectedItem) return;
            onSelect?.({ id: selectedItem.id, path: buildPath(navigationStack, selectedItem) });
            setNavigationStack([]);
            setSelectedItem(null);
          }}
          disabled={!selectedItem}
        >
          {t('locationPicker.selectButton')}
        </DashboardButton>
      </Modal.Footer>
    </Modal>
  );
};

export default memo(SelectLocationModal);
